import { normalizeIdArray } from '../../utils/collectionUtils';
import {
  createEmptyContactMethod,
  normalizeContactMethods,
} from '../../utils/contactMethods';

export const supplierFormSections = [
  {
    title: 'Company Info',
    fields: [
      { name: 'companyName', label: 'Company Name', required: true },
      { name: 'companyEmail', label: 'Company Email', type: 'email' },
      { name: 'website', label: 'Website' },
      { name: 'address', label: 'Address', type: 'textarea' },
      { name: 'mapsLink', label: 'Google Maps Link' },
    ],
  },
  {
    title: 'Social Media',
    fields: [
      { name: 'socials.facebook', label: 'Facebook URL' },
      { name: 'socials.instagram', label: 'Instagram URL' },
      { name: 'socials.tiktok', label: 'TikTok URL' },
      { name: 'socials.linkedin', label: 'LinkedIn URL' },
      { name: 'socials.telegram', label: 'Telegram URL' },
    ],
  },
  {
    title: 'Notes',
    fields: [
      { name: 'comments', label: 'Comments', type: 'textarea' },
    ],
  },
];

export const createEmptySupplierForm = () => ({
  companyName: '',
  contactMethods: [createEmptyContactMethod()],
  companyEmail: '',
  website: '',
  address: '',
  mapsLink: '',
  comments: '',
  categories: [],
  socials: {
    facebook: '',
    instagram: '',
    tiktok: '',
    linkedin: '',
    telegram: '',
  },
});

export const getFieldValue = (form, fieldName) =>
  fieldName.split('.').reduce((value, key) => value?.[key], form) || '';

export const setFieldValue = (form, fieldName, value) => {
  const [firstKey, secondKey] = fieldName.split('.');

  if (!secondKey) {
    return {
      ...form,
      [firstKey]: value,
    };
  }

  return {
    ...form,
    [firstKey]: {
      ...(form[firstKey] || {}),
      [secondKey]: value,
    },
  };
};

export const normalizeUrlValue = (value) => {
  const trimmedValue = typeof value === 'string' ? value.trim() : '';

  if (!trimmedValue) {
    return '';
  }

  return /^https?:\/\//i.test(trimmedValue)
    ? trimmedValue
    : `https://${trimmedValue}`;
};

const isValidOptionalUrl = (value) => {
  const normalizedUrl = normalizeUrlValue(value);

  if (!normalizedUrl) {
    return true;
  }

  try {
    const parsedUrl = new URL(normalizedUrl);
    return ['http:', 'https:'].includes(parsedUrl.protocol) && Boolean(parsedUrl.hostname);
  } catch {
    return false;
  }
};

export const getSupplierFormInitialData = (initialData) => {
  const emptyForm = createEmptySupplierForm();

  if (!initialData) {
    return emptyForm;
  }

  return {
    ...emptyForm,
    companyName: initialData.companyName || initialData.name || '',
    contactMethods: normalizeContactMethods(initialData, { requireAtLeastOne: true }),
    companyEmail: initialData.companyEmail || initialData.email || '',
    website: initialData.website || '',
    address: initialData.address || '',
    mapsLink: initialData.mapsLink || '',
    comments: initialData.comments || '',
    categories: normalizeIdArray(initialData.categories),
    socials: {
      facebook: initialData.socials?.facebook || initialData.facebook || '',
      instagram: initialData.socials?.instagram || initialData.instagram || '',
      tiktok: initialData.socials?.tiktok || initialData.tiktok || '',
      linkedin: initialData.socials?.linkedin || initialData.linkedin || '',
      telegram: initialData.socials?.telegram || initialData.telegram || '',
    },
  };
};

export const validateSupplierForm = (form) => {
  if (!String(form.companyName || '').trim()) {
    return 'Company Name is required.';
  }

  const contactMethods = normalizeContactMethods(form, { requireAtLeastOne: true });

  if (contactMethods.length === 0) {
    return 'At least one contact method is required.';
  }

  const invalidContactMethod = (Array.isArray(form.contactMethods) ? form.contactMethods : [])
    .find((method) => !String(method?.number || '').trim());

  if (invalidContactMethod) {
    return 'Contact method number is required.';
  }

  const urlFields = [
    { label: 'Website', value: form.website },
    { label: 'Google Maps Link', value: form.mapsLink },
    { label: 'Facebook URL', value: form.socials?.facebook },
    { label: 'Instagram URL', value: form.socials?.instagram },
    { label: 'TikTok URL', value: form.socials?.tiktok },
    { label: 'LinkedIn URL', value: form.socials?.linkedin },
    { label: 'Telegram URL', value: form.socials?.telegram },
  ];
  const invalidUrlField = urlFields.find((field) => !isValidOptionalUrl(field.value));

  if (invalidUrlField) {
    return `${invalidUrlField.label} must be a valid URL.`;
  }

  return '';
};

export const buildSupplierFormPayload = (form) => ({
  companyName: String(form.companyName || '').trim(),
  contactMethods: normalizeContactMethods(form, { requireAtLeastOne: true }),
  companyEmail: String(form.companyEmail || '').trim(),
  website: normalizeUrlValue(form.website),
  address: String(form.address || '').trim(),
  mapsLink: normalizeUrlValue(form.mapsLink),
  comments: String(form.comments || '').trim(),
  categories: normalizeIdArray(form.categories),
  socials: {
    facebook: normalizeUrlValue(form.socials?.facebook),
    instagram: normalizeUrlValue(form.socials?.instagram),
    tiktok: normalizeUrlValue(form.socials?.tiktok),
    linkedin: normalizeUrlValue(form.socials?.linkedin),
    telegram: normalizeUrlValue(form.socials?.telegram),
  },
});
